import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import { Outlet } from 'react-router-dom'
import { Alert, AlertColor, Collapse, CssBaseline, Paper as MuiPaper } from '@mui/material'
import { useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { spacing } from '@mui/system'
import GlobalStyle from '../components/GlobalStyle'
import Navbar from '../components/navbar/Navbar'
import Sidebar from '../components/sidebar/Sidebar'
import dashboardItems from '../components/sidebar/dashboardItems'
import Footer from '../components/Footer'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../redux'
import { createNotification } from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import ActionRequestDashboard from '../components/ActionRequestDashboard'

const drawerWidth = 258

const Root = styled('div')`
  display: flex;
  min-height: 100vh;
`

const AppContent = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`

const Paper = styled(MuiPaper)(spacing)

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};
  border-radius: 0;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
`

const Dashboard: React.FC = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const dispatch = useDispatch()
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }
  const { notification } = useSelector((state: RootState) => state.notifications)

  const handleClear = () => {
    dispatch(
      createNotification({
        show: false,
        type: '',
        message: '',
        timeout: 0
      })
    )
  }

  useEffect(() => {
    if (
      notification?.type === NotificationType.SUCCESS ||
      notification?.type === NotificationType.WARNING ||
      notification?.type === NotificationType.INFO
    ) {
      setTimeout(() => {
        handleClear()
      }, notification.timeout)
    } else {
      return
    }
  }, [notification?.show])

  const sev = notification?.type as AlertColor
  const theme = useTheme()
  //const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const navbarHeight = isMobile ? 56 : 64

  return (
    <Root style={{ maxHeight: '100vh', overflowY: 'hidden' }}>
      <CssBaseline />
      <GlobalStyle />

      <>
        {isMobile ? (
          <Sidebar
            key={location.pathname}
            variant="temporary"
            PaperProps={{ style: { width: drawerWidth } }}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems}
          />
        ) : (
          <Sidebar
            variant="permanent"
            PaperProps={{ style: { width: drawerWidth } }}
            items={dashboardItems}
          />
        )}
      </>

      <AppContent
        style={{
          maxHeight: '100vh',
          overflowY: 'hidden',
          marginLeft: isMobile ? 0 : `${drawerWidth}px`
        }}
      >
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <MainContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: `calc(100vh - ${navbarHeight}px)`,
            overflowY: 'hidden'
          }}
        >
          <MainContent
            id="main"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              p: isMobile ? 5 : 12,
              // minHeight: 'calc(100% - 56px)',
              // height: '100%',
              overflowY: 'auto'
            }}
          >
            <Collapse
              in={notification?.show}
              sx={{
                zIndex: 2,
                position: 'fixed',
                bottom: 68,
                right: 24,
                left: isMobile ? 24 : 256 + 24,
                width: 'auto'
              }}
            >
              <Alert variant={'filled'} severity={sev} onClose={() => handleClear()}>
                {notification?.message}
              </Alert>
            </Collapse>

            <ActionRequestDashboard />

            {children}
            <Outlet />
          </MainContent>
          <Footer />
        </MainContent>
      </AppContent>
    </Root>
  )
}

export default Dashboard
